import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Terminal, Sun, Moon } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Navbar({ theme, toggleTheme }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { href: "/#about", label: "About" },
    { href: "/#experience", label: "Experience" },
    { href: "/#education", label: "Education" },
    { href: "/#projects", label: "Projects" },
    { href: "/#skills", label: "Skills" },
    { href: "/#certifications", label: "Certs" },
    { href: "/#achievements", label: "Achievements" },
    { href: "/gallery", label: "Gallery" },
    { href: "/blog", label: "Blog" },
    { href: "/#contact", label: "Contact" }
  ];

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-ink-950/70 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-home-link">
          <Terminal className="w-5 h-5 text-volt group-hover:text-white transition-colors" />
          <span className="font-display font-black tracking-tighter text-lg">
            SKP<span className="text-volt">.</span>
          </span>
          <span className="hidden sm:inline overline ml-2">/ AGENTIC.AI</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-5">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm text-fog-300 hover:text-volt transition-colors tracking-wider uppercase whitespace-nowrap"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
          
          <div className="flex items-center gap-2">
            <button 
              onClick={toggleTheme} 
              className="p-2 text-fog-300 hover:text-volt transition-colors"
              title="Toggle Theme"
            >
              {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </button>

            {user ? (
              <div className="flex items-center gap-3">
                <NavLink to="/admin" className="btn-ghost" data-testid="nav-admin-link">Admin</NavLink>
                <button onClick={logout} className="btn-ghost" data-testid="nav-logout-btn">Logout</button>
              </div>
            ) : (
              <a href="#contact" className="btn-volt" data-testid="nav-cta">Let's Talk</a>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-4 xl:hidden">
          <button 
            onClick={toggleTheme} 
            className="p-2 text-fog-300 hover:text-volt transition-colors"
            title="Toggle Theme"
          >
            {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </button>
          <button
            className="text-fog-100 p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-white/10 bg-ink-950/95 backdrop-blur-xl" data-testid="nav-mobile-menu">
          <div className="px-6 py-5 flex flex-col gap-5">
            {links.map((l) => (
              <a key={l.href} href={l.href} className="py-2 text-fog-100 hover:text-volt uppercase tracking-wider text-sm">
                {l.label}
              </a>
            ))}
            {user ? (
              <>
                <Link to="/admin" className="btn-ghost">Admin</Link>
                <button onClick={logout} className="btn-ghost">Logout</button>
              </>
            ) : (
              <a href="#contact" className="btn-volt">Let's Talk</a>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
